import { forwardRef, useCallback, useId, useRef, type ChangeEvent, type ClipboardEvent } from 'react';
import { cn } from '../../utils/cn';
import { useHoldRepeat } from '../../hooks/useHoldRepeat';
import { IconButton } from './IconButton';
import { Icon } from './Icon';
import { FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES } from './fieldStyles';

export interface NumberStepperProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    label?: string;
    className?: string;
}

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

function parseValue(raw: string): number | null {
    if (raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export const NumberStepper = forwardRef<HTMLInputElement, NumberStepperProps>(function NumberStepper(
    { value, onChange, min = -Infinity, max = Infinity, step = 1, disabled = false, label, className },
    ref,
) {
    const generatedId = useId();
    const inputId = generatedId;

    // Gate on whether any movement is possible, not on whether a whole step
    // fits. `decrement`/`increment` already clamp, so with step=10, max=100
    // and value=95 the old predicate disabled `+` and left 100 unreachable.
    const canDecrement = !disabled && value > min;
    const canIncrement = !disabled && value < max;
    const canDecrementRef = useRef(canDecrement);
    canDecrementRef.current = canDecrement;
    const canIncrementRef = useRef(canIncrement);
    canIncrementRef.current = canIncrement;

    const decrement = useCallback(() => {
        onChange(clamp(value - step, min, max));
    }, [onChange, value, step, min, max]);
    const increment = useCallback(() => {
        onChange(clamp(value + step, min, max));
    }, [onChange, value, step, min, max]);

    const decrementHold = useHoldRepeat(decrement, canDecrementRef);
    const incrementHold = useHoldRepeat(increment, canIncrementRef);

    function commit(raw: string) {
        const parsed = parseValue(raw);
        if (parsed === null) {
            onChange(clamp(value, min, max));
            return;
        }
        onChange(clamp(parsed, min, max));
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        commit(e.target.value);
    }

    function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
        e.preventDefault();
        commit(e.clipboardData.getData('text'));
    }

    return (
        <div className={cn('inline-flex items-center gap-1.5', className)}>
            <IconButton
                type="button"
                size="sm"
                variant="secondary"
                aria-label={label ? `Decrease ${label}` : 'Decrease value'}
                disabled={!canDecrement}
                onClick={decrement}
                {...decrementHold}
            >
                <Icon name="minus" size={14} />
            </IconButton>
            <input
                ref={ref}
                id={inputId}
                type="number"
                inputMode="numeric"
                aria-label={label}
                value={value}
                min={Number.isFinite(min) ? min : undefined}
                max={Number.isFinite(max) ? max : undefined}
                step={step}
                disabled={disabled}
                onChange={handleChange}
                onPaste={handlePaste}
                className={cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'w-16 px-1 text-center')}
            />
            <IconButton
                type="button"
                size="sm"
                variant="secondary"
                aria-label={label ? `Increase ${label}` : 'Increase value'}
                disabled={!canIncrement}
                onClick={increment}
                {...incrementHold}
            >
                <Icon name="plus" size={14} />
            </IconButton>
        </div>
    );
});

export default NumberStepper;
